import { type KnownMistake, type Question } from './model'

/**
 * Deterministic grading of DEMO practice answers, one rule per answer kind,
 * following each subject's conventions (Exercise Framework §6): English text
 * ignores case and extra spaces; Mathematics accepts equivalent fractions;
 * Physics numbers allow a tolerance and a decimal comma; Chemistry formulas
 * are case-sensitive (Co ≠ CO). Runs in the browser only because the content
 * is DEMO; real answer keys and scoring stay server-side.
 */

export interface GradeResult {
  readonly correct: boolean
  readonly display: string
  readonly correctDisplay: string
  readonly mistake: KnownMistake | null
}

const SUBSCRIPTS = '₀₁₂₃₄₅₆₇₈₉'

/** "12,5" or "12.5" → 12.5; null when it is not a plain number. */
export function parseNumber(raw: string): number | null {
  const text = raw.trim().replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null
  return Number(text)
}

/** "5/6" → [5, 6]; null unless both parts are integers and the bottom is not 0. */
export function parseFraction(raw: string): readonly [number, number] | null {
  const match = /^\s*(-?\d+)\s*\/\s*(\d+)\s*$/.exec(raw)
  if (match === null) return null
  const num = Number(match[1])
  const den = Number(match[2])
  return den === 0 ? null : [num, den]
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b)
}

function isSimplest(num: number, den: number): boolean {
  return gcd(num, den) === 1
}

/** Removes spaces and turns subscript digits into plain digits. */
export function normalizeFormula(raw: string): string {
  return raw.replace(/\s+/g, '').replace(/[₀-₉]/g, (c) => String(SUBSCRIPTS.indexOf(c)))
}

/** "CH4" → "CH₄": digits written as subscripts for display. */
export function formatFormula(formula: string): string {
  return formula.replace(/\d/g, (d) => SUBSCRIPTS[Number(d)] ?? d)
}

function normalizeText(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** True when the raw answer can be checked at all (filled in, well formed). */
export function isAnswerComplete(question: Question, raw: string): boolean {
  switch (question.kind) {
    case 'choice':
      return question.options.some((o) => o.id === raw)
    case 'text':
    case 'formula':
      return raw.trim() !== ''
    case 'fraction':
      return parseFraction(raw) !== null
    case 'number':
      return parseNumber(raw) !== null
  }
}

/** The answer as the learner reads it. */
export function displayAnswer(question: Question, raw: string): string {
  switch (question.kind) {
    case 'choice':
      return question.options.find((o) => o.id === raw)?.text ?? raw
    case 'text':
      return raw.trim()
    case 'formula':
      return formatFormula(normalizeFormula(raw))
    case 'fraction': {
      const f = parseFraction(raw)
      return f === null ? raw : `${String(f[0])}/${String(f[1])}`
    }
    case 'number': {
      const n = parseNumber(raw)
      const value = n === null ? raw.trim() : String(n).replace('.', ',')
      return question.unit === null ? value : `${value} ${question.unit}`
    }
  }
}

function correctDisplay(question: Question): string {
  switch (question.kind) {
    case 'choice':
      return displayAnswer(question, question.correctOptionId)
    case 'text':
      return question.accepted[0] ?? ''
    case 'formula':
      return formatFormula(question.accepted[0] ?? '')
    case 'fraction':
      return `${String(question.numerator)}/${String(question.denominator)}`
    case 'number':
      return displayAnswer(question, String(question.value))
  }
}

function isCorrect(question: Question, raw: string): boolean {
  switch (question.kind) {
    case 'choice':
      return raw === question.correctOptionId
    case 'text':
      return question.accepted.some((a) => normalizeText(a) === normalizeText(raw))
    case 'formula':
      return question.accepted.includes(normalizeFormula(raw))
    case 'fraction': {
      const f = parseFraction(raw)
      return f !== null && f[0] * question.denominator === question.numerator * f[1]
    }
    case 'number': {
      const n = parseNumber(raw)
      return n !== null && Math.abs(n - question.value) <= question.tolerance
    }
  }
}

/** Matches a wrong answer to a known mistake, with the kind's own comparison. */
function findMistake(question: Question, raw: string): KnownMistake | null {
  const same = (known: string): boolean => {
    switch (question.kind) {
      case 'choice':
        return known === raw
      case 'text':
        return normalizeText(known) === normalizeText(raw)
      case 'formula':
        return known === normalizeFormula(raw)
      case 'fraction': {
        const a = parseFraction(known)
        const b = parseFraction(raw)
        return a !== null && b !== null && a[0] * b[1] === b[0] * a[1]
      }
      case 'number': {
        const a = parseNumber(known)
        const b = parseNumber(raw)
        return a !== null && b !== null && Math.abs(a - b) <= question.tolerance
      }
    }
  }
  return question.mistakes?.find((m) => same(m.answer)) ?? null
}

export function gradeAnswer(question: Question, raw: string): GradeResult {
  const correct = isCorrect(question, raw)
  return {
    correct,
    display: displayAnswer(question, raw),
    correctDisplay: correctDisplay(question),
    mistake: correct ? null : findMistake(question, raw),
  }
}

/** For a correct but unsimplified fraction: the simplest form, else null. */
export function simplerFraction(question: Question, raw: string): string | null {
  if (question.kind !== 'fraction') return null
  const f = parseFraction(raw)
  if (f === null || isSimplest(f[0], f[1])) return null
  const g = gcd(f[0], f[1])
  return `${String(f[0] / g)}/${String(f[1] / g)}`
}
